import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Enums, ExtensionManager } from '@ohif/core';
import { extensionManager } from '../App';
import { useParams, useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import useSearchParams from '../hooks/useSearchParams';

const areLocationsTheSame = (location0, location1) => {
  return (
    location0.pathname === location1.pathname &&
    location0.search === location1.search &&
    location0.hash === location1.hash
  );
};

const DEFAULT_DATA = {
  studies: [],
  total: 0,
  resultsPerPage: 25,
  pageNumber: 1,
  location: '',
};

// ✅ Sample Hardcoded Studies
const SAMPLE_STUDIES = [
  {
    StudyInstanceUID: '1.2.3.4.5',
    StudyDate: '20250512',
    StudyDescription: 'CT Chest Sample',
    PatientName: 'DOE^JOHN',
    PatientID: '12345',
    ModalitiesInStudy: 'CT',
    AccessionNumber: 'A0001',
  },
  {
    StudyInstanceUID: '5.4.3.2.1',
    StudyDate: '20250510',
    StudyDescription: 'MRI Brain Sample',
    PatientName: 'SMITH^JANE',
    PatientID: '67890',
    ModalitiesInStudy: 'MR',
    AccessionNumber: 'A0002',
  },
];

function DataSourceWrapper(props) {
  const { servicesManager, children: LayoutTemplate } = props;
  const params = useParams();
  const location = useLocation();
  const lowerCaseSearchParams = useSearchParams({ lowerCaseKeys: true });

  const getInitialDataSourceName = useCallback(() => {
    const dataSourceName = lowerCaseSearchParams.get('datasources');
    return dataSourceName || '';
  }, []);

  const [dataSource, setDataSource] = useState(() => {
    const dataSourceName = getInitialDataSourceName();
    return dataSourceName
      ? extensionManager.getDataSources(dataSourceName)?.[0]
      : extensionManager.getActiveDataSource()[0];
  });

  const [data, setData] = useState(DEFAULT_DATA);
  const [isDataSourceInitialized, setIsDataSourceInitialized] = useState(false);

  useEffect(() => {
    const initializeDataSource = async () => {
      await dataSource.initialize({ params });
      setIsDataSourceInitialized(true);
    };
    initializeDataSource();
  }, [dataSource, params]);

  useEffect(() => {
    const handleDataSourceChange = () => {
      setDataSource(extensionManager.getActiveDataSource()[0]);
      setIsDataSourceInitialized(false);
      setData(DEFAULT_DATA);
    };

    const sub = extensionManager.subscribe(
      ExtensionManager.EVENTS.ACTIVE_DATA_SOURCE_CHANGED,
      handleDataSourceChange
    );
    return () => sub.unsubscribe();
  }, []);

  // ✅ Set hardcoded sample data once initialized
  useEffect(() => {
    if (!isDataSourceInitialized) return;

    setData({
      studies: SAMPLE_STUDIES,
      total: SAMPLE_STUDIES.length,
      resultsPerPage: 25,
      pageNumber: 1,
      location,
    });
  }, [isDataSourceInitialized, location]);

  return (
    <LayoutTemplate
      studies={data.studies}
      total={data.total}
      pageNumber={data.pageNumber}
      resultsPerPage={data.resultsPerPage}
      dataSource={dataSource}
      isLoading={false}
    />
  );
}

DataSourceWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  servicesManager: PropTypes.object.isRequired,
};

export default DataSourceWrapper;
